// Request and response types for the referral server functions.
//
// See design-docs/referral-program.md.
import { ReferralClaimRefusal, ReferralFriendStatus } from './data-model/referral';

// --- referralGetOrCreateCode (authenticated) ---

export interface ReferralGetOrCreateCodeParams {
  // deliberately empty: the caller is identified by their authentication, and the
  // link is built from the server's own domain rather than anything a client sends
}

export interface ReferralGetOrCreateCodeResult {
  /** The readable code as stored (8 characters). Anything that builds an invite link uses this one. */
  code: string;
  /**
   * The code as people are shown it: `code` plus its check character (9 characters, #6984), so a
   * mistyped character is caught. Missing from servers older than #6984; show `code` then.
   */
  displayCode?: string;
  /** The unguessable half of the link. Returned so the client can rebuild the link if it needs to. */
  linkKey: string;
  /** The whole invite link, built server-side from the real app domain. */
  inviteLink: string;
  /**
   * Whether this caller already has somebody recorded as the friend who invited
   * THEM. It is about the caller as a friend, not as an inviter, and it is here
   * because the hub page needs it: the "were you invited?" code box is shown only
   * to somebody who has no inviter yet, since offering it to anybody else invites
   * them to type a second code that is certain to be refused.
   */
  hasReferrer: boolean;
}

// --- referralPeekInvite (PUBLIC — see PUBLIC_API_FUNCTIONS) ---

export interface ReferralPeekInviteParams {
  /** The whole `<CODE>.<LINKKEY>` value out of the link's `ref` query parameter. */
  ref: string;
}

/**
 * What the invite page is told.
 *
 * There is deliberately no reason field and no distinction between failures. A
 * code that does not exist, a link key that does not match, and a feature switched
 * off for that inviter all answer `{ valid: false }` and nothing else. Anything
 * else would turn a public endpoint into a way of finding out which codes exist.
 * The same applies to the HTTP status: both must answer the same way.
 */
export interface ReferralPeekInviteResult {
  valid: boolean;
  /** Only ever present when `valid` is true. */
  inviterFirstName?: string;
}

// --- referralClaim (authenticated) ---

export interface ReferralClaimParams {
  /** Either the full `<CODE>.<LINKKEY>` value, or a bare code somebody typed by hand. */
  ref: string;
}

export interface ReferralClaimResult {
  /** True when the caller now has a referral record — whether this call wrote it or an earlier one did. */
  claimed: boolean;
  /**
   * Why not, when `claimed` is false.
   *
   * Unlike the public peek above, naming the reason here is safe and useful: the
   * caller is signed in, and every refusal is about their OWN account, so it tells
   * them nothing about anybody else. It is what lets the hub say "you cannot use
   * your own code" rather than a shrug.
   */
  refusal?: ReferralClaimRefusal;
}

// --- referralReportTraining (authenticated) ---

export interface ReferralReportTrainingParams {
  // deliberately empty: the caller is identified by their authentication, and the
  // only record this can ever touch is that caller's own. A client that could name
  // a record would be a client that could flag a stranger's.
}

/** What became of a training report. Exactly one of these, and none of them is an error. */
export enum ReferralTrainingOutcome {
  /** This call set the flag. */
  Recorded = 'recorded',
  /** An earlier call already set it. The caller may stop asking, for good. */
  AlreadyRecorded = 'already-recorded',
  /**
   * The caller has no referral record, so there is nothing to flag.
   *
   * A NORMAL answer, not a failure. The client asks only when it holds a local
   * marker saying it was referred, and that marker and the server's records can
   * disagree — restoring a device backup is the ordinary way it happens. The
   * client's right response is to drop its marker and stop asking; it is NOT a
   * problem to report.
   */
  NoReferral = 'no-referral',
}

export interface ReferralReportTrainingResult {
  outcome: ReferralTrainingOutcome;
}

// --- referralListFriends (authenticated) ---

export interface ReferralListFriendsParams {
  // deliberately empty: the caller is identified by their authentication, and they
  // may only ever ask about their own friends
}

/**
 * One friend, as their inviter is allowed to see them.
 *
 * **Two fields, and that is the whole point of this type.** No email, no account
 * id, no dates, no counts. Anything added here is shown to somebody other than the
 * person it is about, so a new field is a fresh decision about their privacy — not
 * a convenience for whoever is writing the page.
 */
export interface ReferralFriendRow {
  /**
   * The friend's first name, read from their account at the moment this answer was
   * built — so somebody who changes their name is not shown under an old one.
   *
   * **Empty when they have set no name at all.** The stand-in wording is left to
   * the client on purpose: the hub is a signed-in page shown in the reader's own
   * language, and a word chosen here would arrive in English inside a Dutch list.
   */
  firstName: string;
  status: ReferralFriendStatus;
}

/**
 * The inviter's friends, newest invite first.
 *
 * There is no separate count field: the count IS the length of this list, and two
 * numbers that must agree are two numbers that can disagree.
 */
export interface ReferralListFriendsResult {
  friends: ReferralFriendRow[];
}
